// src/admin/services/api.js
import axios from "axios";

const API_BASE_URL =
  process.env.NODE_ENV === "development"
    ? (process.env.REACT_APP_API_URL && process.env.REACT_APP_API_URL !== "/api"
        ? process.env.REACT_APP_API_URL
        : "http://localhost:5001/api")
    : (process.env.REACT_APP_API_URL || "/api");

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Attach Authorization header if token exists in localStorage (supports both cookies and bearer header)
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("selvas_admin_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response error handler
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const message =
      error.response?.data?.message || error.message || "An error occurred with the server.";
    return Promise.reject(new Error(message));
  }
);

// ================= AUTH APIs =================
export const authAPI = {
  login: async (email, password, rememberMe = false) => {
    const res = await api.post("/admin/auth/login", { email, password, rememberMe });
    return res.data;
  },
  logout: async () => {
    const res = await api.post("/admin/auth/logout");
    return res.data;
  },
  getMe: async () => {
    const res = await api.get("/admin/auth/me");
    return res.data;
  },
  changePassword: async (currentPassword, newPassword) => {
    const res = await api.put("/admin/auth/change-password", {
      currentPassword,
      newPassword,
    });
    return res.data;
  },
  updateProfile: async (profileData) => {
    const res = await api.put("/admin/auth/profile", profileData);
    return res.data;
  },
};

// ================= DASHBOARD APIs =================
export const dashboardAPI = {
  getStats: async () => {
    const res = await api.get("/dashboard/stats");
    return res.data;
  },
  getRecent: async () => {
    const res = await api.get("/dashboard/recent");
    return res.data;
  },
};

// ================= DAILY CURRENT AFFAIRS APIs =================
export const dailyCAAPI = {
  getAll: async (params = {}) => {
    const res = await api.get("/current-affairs/daily", { params });
    return res.data;
  },
  getById: async (id) => {
    const res = await api.get(`/current-affairs/daily/${id}`);
    return res.data;
  },
  create: async (formData) => {
    const res = await api.post("/current-affairs/daily", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data;
  },
  update: async (id, formData) => {
    const res = await api.put(`/current-affairs/daily/${id}`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data;
  },
  delete: async (id) => {
    const res = await api.delete(`/current-affairs/daily/${id}`);
    return res.data;
  },
  toggleStatus: async (id, status) => {
    const res = await api.patch(`/current-affairs/daily/${id}/status`, { status });
    return res.data;
  },
};

// ================= MONTHLY CURRENT AFFAIRS APIs =================
export const monthlyCAAPI = {
  getAll: async (params = {}) => {
    const res = await api.get("/current-affairs/monthly", { params });
    return res.data;
  },
  getById: async (id) => {
    const res = await api.get(`/current-affairs/monthly/${id}`);
    return res.data;
  },
  create: async (formData) => {
    const res = await api.post("/current-affairs/monthly", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data;
  },
  update: async (id, formData) => {
    const res = await api.put(`/current-affairs/monthly/${id}`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data;
  },
  delete: async (id) => {
    const res = await api.delete(`/current-affairs/monthly/${id}`);
    return res.data;
  },
  toggleStatus: async (id, status) => {
    const res = await api.patch(`/current-affairs/monthly/${id}/status`, { status });
    return res.data;
  },
};

// ================= QUESTION BANK APIs =================
export const qbAPI = {
  getFolders: async (type = "") => {
    const res = await api.get("/question-bank/folders", {
      params: type ? { type } : {},
    });
    return res.data;
  },
  createFolder: async (data) => {
    const res = await api.post("/question-bank/folders", data);
    return res.data;
  },
  updateFolder: async (id, data) => {
    const res = await api.put(`/question-bank/folders/${id}`, data);
    return res.data;
  },
  deleteFolder: async (id) => {
    const res = await api.delete(`/question-bank/folders/${id}`);
    return res.data;
  },
  getQuestions: async (params = {}) => {
    const res = await api.get("/question-bank/questions", { params });
    return res.data;
  },
  getQuestionById: async (id) => {
    const res = await api.get(`/question-bank/questions/${id}`);
    return res.data;
  },
  createQuestion: async (data) => {
    const res = await api.post("/question-bank/questions", data);
    return res.data;
  },
  updateQuestion: async (id, data) => {
    const res = await api.put(`/question-bank/questions/${id}`, data);
    return res.data;
  },
  deleteQuestion: async (id) => {
    const res = await api.delete(`/question-bank/questions/${id}`);
    return res.data;
  },
  moveQuestions: async (questionIds, targetFolderId) => {
    const res = await api.post("/question-bank/questions/move", {
      questionIds,
      targetFolderId,
    });
    return res.data;
  },
  parseBulkUpload: async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    const res = await api.post("/question-bank/parse-upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data;
  },
  confirmBulk: async (data) => {
    const res = await api.post("/question-bank/confirm-bulk", data);
    return res.data;
  },
};

// ================= PRELIMS INITIATIVE APIs =================
export const prelimsAPI = {
  getAll: async (params = {}) => {
    const res = await api.get("/prelims", { params });
    return res.data;
  },
  getById: async (id) => {
    const res = await api.get(`/prelims/${id}`);
    return res.data;
  },
  create: async (data) => {
    const res = await api.post("/prelims", data);
    return res.data;
  },
  update: async (id, data) => {
    const res = await api.put(`/prelims/${id}`, data);
    return res.data;
  },
  delete: async (id) => {
    const res = await api.delete(`/prelims/${id}`);
    return res.data;
  },
  toggleStatus: async (id, status) => {
    const res = await api.patch(`/prelims/${id}/status`, { status });
    return res.data;
  },
  submitAttempt: async (testId, attemptData) => {
    const res = await api.post(`/prelims/${testId}/attempt`, attemptData);
    return res.data;
  },
};

// ================= MAINS INITIATIVE APIs =================
export const mainsAPI = {
  getAll: async (params = {}) => {
    const res = await api.get("/mains", { params });
    return res.data;
  },
  getById: async (id) => {
    const res = await api.get(`/mains/${id}`);
    return res.data;
  },
  create: async (formData) => {
    const res = await api.post("/mains", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data;
  },
  update: async (id, formData) => {
    const res = await api.put(`/mains/${id}`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data;
  },
  delete: async (id) => {
    const res = await api.delete(`/mains/${id}`);
    return res.data;
  },
  toggleStatus: async (id, status) => {
    const res = await api.patch(`/mains/${id}/status`, { status });
    return res.data;
  },
  submitAnswer: async (initiativeId, formData) => {
    const res = await api.post(`/mains/${initiativeId}/submit`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data;
  },
  evaluateSubmission: async (submissionId, evalData) => {
    const res = await api.put(`/mains/submissions/${submissionId}/evaluate`, evalData);
    return res.data;
  },
  togglePublish: async (submissionId, isResultPublished) => {
    const res = await api.patch(`/mains/submissions/${submissionId}/publish`, {
      isResultPublished,
    });
    return res.data;
  },
};

// ================= RESULTS APIs =================
export const resultsAPI = {
  getPrelims: async (params = {}) => {
    const res = await api.get("/results/prelims", { params });
    return res.data;
  },
  getPrelimsById: async (id) => {
    const res = await api.get(`/results/prelims/${id}`);
    return res.data;
  },
  getMains: async (params = {}) => {
    const res = await api.get("/results/mains", { params });
    return res.data;
  },
  getMainsById: async (id) => {
    const res = await api.get(`/results/mains/${id}`);
    return res.data;
  },
  getStudentProfile: async (idOrEmail) => {
    const res = await api.get(`/results/students/${encodeURIComponent(idOrEmail)}/profile`);
    return res.data;
  },
};

export default api;
